import { useCallback, useState } from 'react'
import type { UserStatus } from '@olmt/shared'
import { api, describeError, type AdminUser } from '../lib/api'
import { formatPresence } from '../lib/format'
import { useAuthStore } from '../stores/authStore'
import { useLoad } from '../hooks/useLoad'
import { Avatar } from '../components/Avatar'
import { CopyButton } from '../components/CopyButton'
import { CredentialsForm, type CredentialsPatch } from '../components/CredentialsForm'
import { IconButton } from '../components/IconButton'
import { CloseIcon, RefreshIcon, SearchIcon, TrashIcon } from '../components/Icons'
import { SectionHeading } from '../components/SectionHeading'
import { Spinner } from '../components/Spinner'
import { StatusPill } from '../components/StatusPill'

type MemberFacts = { deviceCount: number; online: boolean }
type UsersData = { users: AdminUser[]; facts: Map<string, MemberFacts> }

/** One row has one open mode at a time — the console's version of a two-step confirm. */
type RowMode =
  | { kind: 'idle' }
  | { kind: 'delete'; userId: string }
  | { kind: 'admin'; userId: string; next: boolean }
  | { kind: 'credentials'; userId: string }

const FILTERS: { value: UserStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'suspended', label: 'Suspended' },
]

/**
 * Every account on the server: `GET /api/admin/users` for the view, `GET /api/admin/members` for
 * the per-user facts it does not carry (device count and live sockets). The two are joined by user
 * id. Status filtering is client-side so the search box and the filter act on one list.
 *
 * Per row, the console can mint a recovery link, promote or demote an administrator, set an email
 * or password, and delete the account — every write goes through `PATCH`/`DELETE` on
 * `/api/admin/*` (docs/CONTRACT.md §4) and every destructive one through a confirm step.
 */
export function UsersPage() {
  const meId = useAuthStore((state) => state.user?.id ?? null)
  const load = useCallback(async (): Promise<UsersData> => {
    const [users, members] = await Promise.all([api.adminUsers(), api.adminMembers()])
    const facts = new Map<string, MemberFacts>()
    for (const member of members.members) {
      facts.set(member.user.id, { deviceCount: member.deviceCount, online: member.online })
    }
    return { users: users.users, facts }
  }, [])
  const { data, loading, error, reload } = useLoad(load, (caught) =>
    describeError(caught, 'Could not load the user list.'),
  )

  const [filter, setFilter] = useState<UserStatus | 'all'>('all')
  const [query, setQuery] = useState('')
  const [mode, setMode] = useState<RowMode>({ kind: 'idle' })
  const [busyId, setBusyId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [recovery, setRecovery] = useState<{ user: AdminUser; url: string } | null>(null)

  const run = async (user: AdminUser, work: () => Promise<void>, done: string) => {
    setBusyId(user.id)
    setActionError(null)
    setNotice(null)
    try {
      await work()
      setMode({ kind: 'idle' })
      setNotice(done)
      reload()
    } catch (caught) {
      setActionError(describeError(caught, 'Could not update that account.'))
    } finally {
      setBusyId(null)
    }
  }

  const mintRecovery = async (user: AdminUser) => {
    setBusyId(user.id)
    setActionError(null)
    try {
      const response = await api.createRecoveryCode(user.id, { expiresInHours: 24 })
      setRecovery({ user, url: response.url })
      reload()
    } catch (caught) {
      setActionError(describeError(caught, 'Could not create a recovery link.'))
    } finally {
      setBusyId(null)
    }
  }

  const setAdmin = (user: AdminUser, next: boolean) =>
    run(
      user,
      () => api.adminUpdateUser(user.id, { isAdmin: next }).then(() => undefined),
      next
        ? `${user.displayName} is now an administrator.`
        : `${user.displayName} is no longer an administrator.`,
    )

  const saveCredentials = (user: AdminUser, patch: CredentialsPatch) =>
    run(
      user,
      () => api.adminUpdateUser(user.id, patch).then(() => undefined),
      `Credentials updated for ${user.displayName}.`,
    )

  const remove = (user: AdminUser) =>
    run(
      user,
      () => api.deleteMember(user.id).then(() => undefined),
      `${user.displayName} was deleted.`,
    )

  const needle = query.trim().toLowerCase()
  const visible =
    data?.users.filter((user) => {
      if (filter !== 'all' && user.status !== filter) return false
      if (needle === '') return true
      return [user.displayName, user.handle, user.email ?? ''].some((field) =>
        field.toLowerCase().includes(needle),
      )
    }) ?? []

  const counts = new Map<string, number>()
  for (const user of data?.users ?? []) counts.set(user.status, (counts.get(user.status) ?? 0) + 1)

  return (
    <section aria-labelledby="users-heading">
      <SectionHeading
        id="users-heading"
        action={
          <IconButton label="Refresh the user list" size={36} onClick={reload} disabled={loading}>
            <RefreshIcon size={16} />
          </IconButton>
        }
      >
        Users
        {data && <span className="numeric ms-2 text-muted">{data.users.length}</span>}
      </SectionHeading>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="flex min-h-11 min-w-56 flex-1 items-center gap-2 rounded-well bg-surface2 px-3.5 ring-1 ring-line ring-inset">
          <SearchIcon size={16} className="shrink-0 text-muted" />
          <span className="sr-only">Search by name, handle or email</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search name, handle or email"
            className="min-w-0 flex-1 bg-transparent text-ui text-ink outline-none placeholder:text-muted"
          />
        </label>
        <div role="group" aria-label="Filter by status" className="flex flex-wrap items-center gap-1">
          {FILTERS.map((item) => (
            <button
              key={item.value}
              type="button"
              aria-pressed={filter === item.value}
              onClick={() => setFilter(item.value)}
              className={`press flex min-h-11 items-center gap-1.5 rounded-full px-3 text-ui ${
                filter === item.value
                  ? 'bg-accent/12 font-semibold text-accent ring-1 ring-accent/30 ring-inset'
                  : 'text-muted hover:bg-surface2 hover:text-ink'
              }`}
            >
              {item.label}
              <span className="numeric text-meta text-muted">
                {item.value === 'all' ? (data?.users.length ?? 0) : (counts.get(item.value) ?? 0)}
              </span>
            </button>
          ))}
        </div>
      </div>

      {loading && !data && (
        <div className="flex justify-center py-10 text-muted">
          <Spinner label="Loading users" />
        </div>
      )}

      {error && (
        <p role="alert" className="text-ui text-danger">
          {error}
        </p>
      )}

      {actionError && (
        <p role="alert" className="mb-2 text-ui text-danger">
          {actionError}
        </p>
      )}

      {notice && (
        <p
          role="status"
          className="mb-3 flex items-center gap-2 rounded-panel border-s-2 border-accent bg-surface2 py-1.5 ps-3 pe-1.5 text-meta text-ink"
        >
          <span className="min-w-0 flex-1">{notice}</span>
          <IconButton label="Dismiss" size={36} onClick={() => setNotice(null)}>
            <CloseIcon size={16} />
          </IconButton>
        </p>
      )}

      {recovery && (
        <p className="mb-3 flex items-center gap-2 rounded-panel border-s-2 border-accent bg-surface2 py-1.5 ps-3 pe-1.5 text-meta text-ink">
          <span className="min-w-0 flex-1 break-all">
            <bdi>{recovery.url}</bdi>
            <span className="block text-muted">
              Recovery link for <bdi>{recovery.user.displayName}</bdi> — single use, expires in 24
              hours. Send it to that person to add a device to their account.
            </span>
          </span>
          <CopyButton value={recovery.url} label="Copy recovery link" />
          <IconButton label="Dismiss recovery link" size={36} onClick={() => setRecovery(null)}>
            <CloseIcon size={16} />
          </IconButton>
        </p>
      )}

      {data && data.users.length === 0 && (
        <div className="rounded-panel bg-surface p-5 ring-1 ring-line ring-inset">
          <p className="text-ui text-ink">No accounts yet.</p>
          <p className="mt-1 text-ui text-muted">
            An account appears here after it signs up in the chat app or redeems an invite.
          </p>
        </div>
      )}

      {data && data.users.length > 0 && visible.length === 0 && (
        <p className="rounded-panel bg-surface p-5 text-ui text-muted ring-1 ring-line ring-inset">
          No account matches this filter.
        </p>
      )}

      {visible.length > 0 && (
        <ul className="flex flex-col gap-2">
          {visible.map((user) => {
            const facts = data?.facts.get(user.id)
            const online = facts?.online ?? user.online
            const isSelf = user.id === meId
            const open = mode.kind !== 'idle' && mode.userId === user.id ? mode : null
            return (
              <li
                key={user.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-panel bg-surface p-3 ring-1 ring-line ring-inset"
              >
                <Avatar name={user.displayName} path={user.avatarUrl} size={40} online={online} />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="truncate text-ui font-medium text-ink">
                      <bdi>{user.displayName}</bdi>
                    </span>
                    {isSelf && (
                      <span className="shrink-0 rounded-full bg-surface2 px-2 py-0.5 text-meta font-medium text-muted ring-1 ring-line-strong ring-inset">
                        you
                      </span>
                    )}
                    <StatusPill status={user.status} />
                    {user.isAdmin && (
                      <span className="shrink-0 rounded-full bg-accent/12 px-2 py-0.5 text-meta font-medium text-accent ring-1 ring-accent/30 ring-inset">
                        admin
                      </span>
                    )}
                  </span>
                  <span className="block truncate text-meta text-muted">
                    @{user.handle}
                    {user.email ? ` · ${user.email}` : ''} ·{' '}
                    {facts ? `${facts.deviceCount} ${facts.deviceCount === 1 ? 'device' : 'devices'}` : '—'} ·{' '}
                    {formatPresence(online, user.lastSeenAt)}
                  </span>
                </span>

                {open?.kind === 'delete' ? (
                  <span className="flex items-center gap-2">
                    <span className="text-meta text-danger">
                      Delete <bdi>{user.displayName}</bdi> and every device?
                    </span>
                    <button
                      type="button"
                      disabled={busyId !== null}
                      onClick={() => void remove(user)}
                      className="press flex min-h-11 items-center gap-2 rounded-full bg-danger px-4 text-ui font-semibold text-on-danger hover:brightness-110 disabled:opacity-50"
                    >
                      {busyId === user.id && <Spinner size={16} label="Deleting" />}
                      Delete
                    </button>
                    <button
                      type="button"
                      onClick={() => setMode({ kind: 'idle' })}
                      className="press min-h-11 rounded-full px-4 text-ui font-medium text-ink ring-1 ring-line ring-inset hover:bg-surface2"
                    >
                      Cancel
                    </button>
                  </span>
                ) : open?.kind === 'admin' ? (
                  <span className="flex items-center gap-2">
                    <span className="text-meta text-ink">
                      {open.next ? (
                        <>
                          Make <bdi>{user.displayName}</bdi> an administrator?
                        </>
                      ) : (
                        <>
                          Remove admin from <bdi>{user.displayName}</bdi>?
                        </>
                      )}
                    </span>
                    <button
                      type="button"
                      disabled={busyId !== null}
                      onClick={() => void setAdmin(user, open.next)}
                      className="press flex min-h-11 items-center gap-2 rounded-full bg-accent px-4 text-ui font-semibold text-on-accent hover:bg-accent-strong disabled:opacity-50"
                    >
                      {busyId === user.id && <Spinner size={16} label="Saving" />}
                      Confirm
                    </button>
                    <button
                      type="button"
                      onClick={() => setMode({ kind: 'idle' })}
                      className="press min-h-11 rounded-full px-4 text-ui font-medium text-ink ring-1 ring-line ring-inset hover:bg-surface2"
                    >
                      Cancel
                    </button>
                  </span>
                ) : open?.kind === 'credentials' ? (
                  <CredentialsForm
                    user={user}
                    busy={busyId === user.id}
                    onSave={(patch) => void saveCredentials(user, patch)}
                    onCancel={() => setMode({ kind: 'idle' })}
                  />
                ) : (
                  <span className="flex flex-wrap items-center gap-1">
                    <button
                      type="button"
                      disabled={busyId !== null}
                      onClick={() => void mintRecovery(user)}
                      className="press flex min-h-11 items-center gap-2 rounded-full px-3 text-ui font-medium text-ink ring-1 ring-line ring-inset hover:bg-surface2 disabled:opacity-50 sm:px-4"
                    >
                      {busyId === user.id && <Spinner size={16} label="Creating recovery link" />}
                      Recovery link
                    </button>
                    <button
                      type="button"
                      disabled={busyId !== null || isSelf}
                      title={isSelf ? 'You cannot change your own admin state.' : undefined}
                      onClick={() => setMode({ kind: 'admin', userId: user.id, next: !user.isAdmin })}
                      className="press min-h-11 rounded-full px-3 text-ui font-medium text-ink ring-1 ring-line ring-inset hover:bg-surface2 disabled:opacity-50 sm:px-4"
                    >
                      {user.isAdmin ? 'Demote' : 'Promote'}
                    </button>
                    <button
                      type="button"
                      disabled={busyId !== null}
                      onClick={() => setMode({ kind: 'credentials', userId: user.id })}
                      className="press min-h-11 rounded-full px-3 text-ui font-medium text-ink ring-1 ring-line ring-inset hover:bg-surface2 disabled:opacity-50 sm:px-4"
                    >
                      Credentials
                    </button>
                    <IconButton
                      label={`Delete ${user.displayName}`}
                      variant="destructive"
                      size={40}
                      onClick={() => setMode({ kind: 'delete', userId: user.id })}
                    >
                      <TrashIcon size={18} />
                    </IconButton>
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
